import {
  Button,
  Dialog,
  DialogContent,
  InfoTooltip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import {
  BaseStationTargetInfo,
  ShipInfo,
  TargetInfoType,
  TargetTypeEnum,
  useDownloadTrack,
  useSession,
  useDisplayMarks,
} from '@ais-ui/websockets';
import { useState } from 'react';
import {
  PiBoatThin,
  PiDownload,
  PiFunnelThin,
  PiMapPinLine,
  PiBroadcastThin,
} from 'react-icons/pi';
import { ShowShip } from './ShowShip';
import { CreateReportTemplate } from '../../Reports/Mutations/CreateReportTemplate/CreateReportTemplate';
import { CreateReportProvider } from '../../Reports/Mutations/CreateReportTemplate/CreateReportContext';
import { ShipReportButton } from './ShipReportButton';
import { ViewMode } from '../../../components/ViewModeProvider';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { useFilterContext } from '../FilterContext';
import { MarkTargetWithColor } from './MarkTargetWithColor';
import { useLocalization } from '@ais-ui/locale-management';
import { useCoverage } from '../Coverage/CoverageContext';
import { usePermissions } from '../../Permissions/PermissionsProvider';

const ActionButton = ({
  id,
  Icon,
  disabled,
  onClick,
  text,
  disabledText,
  ...rest
}: {
  id: string;
  Icon: React.ElementType;
  disabled?: boolean;
  onClick?: () => void;
  text: string;
  disabledText?: string;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const onMouseEnter = () => {
    if (disabled) {
      setIsHovered(true);
    }
  };

  return (
    <div
      onMouseEnter={() => onMouseEnter()}
      onMouseLeave={() => setIsHovered(false)}
      className="col-span-2"
    >
      <Button
        id={id}
        disabled={disabled}
        variant="ghost"
        onClick={onClick}
        {...rest}
        className="flex gap-x-2 col-span-2 justify-start"
      >
        <Icon />
        {text}
      </Button>
      {disabled && isHovered && (
        <InfoTooltip className="-bottom-2">{disabledText}</InfoTooltip>
      )}
    </div>
  );
};

interface TargetActionsProps {
  targetInfo: TargetInfoType;
  targetType: TargetTypeEnum;
  viewMode?: ViewMode;
}

export const TargetActions = ({
  targetInfo,
  targetType,
  viewMode,
}: TargetActionsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isShowVesselActive, setIsShowVesselActive] = useState<boolean>(false);
  const { olSession } = useSession();
  const { targetInfoDetailsList } = useSelectionState();
  const { customFilters, setCustomFilters } = useFilterContext();
  const { toast } = useToast();
  const { unmarkTarget } = useDisplayMarks();
  const { messages } = useLocalization();
  const { findSourceIdentifierByMmsi, onSourceIdentifierSelect } = useCoverage();
  const permissions = usePermissions();

  const { startCsvDownload, startKmlDownload } = useDownloadTrack(olSession);

  const onShowVesselClick = () => {
    setIsShowVesselActive(!isShowVesselActive);
  };

  const onDownloadTrackClick = (fileType: 'csv' | 'kml') => {
    if (targetType === TargetTypeEnum.Ship && fileType === 'csv') {
      startCsvDownload(Number((targetInfo as ShipInfo).mmsi));
    } else if (targetType === TargetTypeEnum.Ship && fileType === 'kml') {
      startKmlDownload(Number((targetInfo as ShipInfo).mmsi));
    }
  };

  const onUnmarkTargetClick = async () => {
    try {
      await unmarkTarget();
      toast({
        title: 'Success',
        description: 'Target unmarked successfully',
        variant: 'default',
      });
      setIsOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to unmark target',
        variant: 'destructive',
      });
    }
  };

  const onFilterBySelectedClick = () => {
    // Extract MMSIs from all selected ships
    const mmsis: string[] = [];
    const shipsWithoutMmsi: string[] = [];

    targetInfoDetailsList.forEach((target) => {
      // Only process Ship type targets
      if (target.extractedTargetInfoType === TargetTypeEnum.Ship) {
        const shipInfo = target.extractedTargetInfo[0] as ShipInfo;
        if (shipInfo.mmsi) {
          mmsis.push(shipInfo.mmsi);
        } else {
          shipsWithoutMmsi.push(shipInfo.name || 'Unknown');
        }
      }
    });

    // Show warning if some ships are missing MMSI
    if (shipsWithoutMmsi.length > 0) {
      toast({
        title: 'Warning',
        description: `${shipsWithoutMmsi.length} ship(s) missing MMSI and will be excluded from filter.`,
        variant: 'default',
      });
    }

    // If no valid MMSIs found, show error and abort
    if (mmsis.length === 0) {
      toast({
        title: 'Error',
        description: messages.targetActionsNoValidMmsis,
        variant: 'destructive',
      });
      return;
    }

    // Create custom filter with OR logic for all MMSIs
    const mmsiFilterParts = mmsis.map((mmsi) => `(mmsi = ${mmsi})`);
    const filterDefinition = mmsiFilterParts.join(' OR ');

    // Create new custom filter object
    const newCustomFilter = {
      enabled: true,
      filter: filterDefinition,
      groups: [],
      isEditable: false,
      name: 'Filter by Selected',
      owner: '',
      uuid: `adhoc-filter-${Date.now()}`,
    };

    // Add to existing custom filters - AISMapLayer effect handles apply and refresh
    setCustomFilters([...customFilters, newCustomFilter]);
    setIsOpen(false);
  };

  const getBaseStationMmsi = (): string | null => {
    const baseStationInfo = targetInfo as BaseStationTargetInfo;
    const mmsi = baseStationInfo.mmsi;

    if (!mmsi) {
      toast({
        title: 'Error',
        description: 'Base station MMSI not available',
        variant: 'destructive',
      });
      return null;
    }
    return mmsi;
  };

  const onFilterByBaseStationClick = () => {
    const mmsi = getBaseStationMmsi();
    if (!mmsi) return;

    // Format the MMSI value - quote if it starts with 0 to preserve leading zeros
    const formattedMmsi = mmsi.startsWith('0') ? `"${mmsi}"` : mmsi;

    // Create custom filter to show ships observed by this base station
    const filterDefinition = `bs_mmsi = ${formattedMmsi}`;

    // Create new custom filter object
    const newCustomFilter = {
      enabled: true,
      filter: filterDefinition,
      groups: [],
      isEditable: false,
      name: `Filter by Base Station ${mmsi}`,
      owner: '',
      uuid: `adhoc-filter-basestation-${Date.now()}`,
    };

    // Add to existing custom filters - AISMapLayer effect handles apply and refresh
    setCustomFilters([...customFilters, newCustomFilter]);
    setIsOpen(false);
  };

  const onShowCoverageClick = async () => {
    const mmsi = getBaseStationMmsi();
    if (!mmsi) return;

    // Find the source identifier by MMSI
    const sourceIdentifier = findSourceIdentifierByMmsi(mmsi);

    if (!sourceIdentifier) {
      toast({
        title: 'Not Found',
        description: `No coverage source identifier found for base station ${mmsi}`,
        variant: 'destructive',
      });
      return;
    }

    // Select the source identifier to show coverage
    await onSourceIdentifierSelect(sourceIdentifier);
    setIsOpen(false);
  };

  if (
    targetType !== TargetTypeEnum.Ship &&
    targetType !== TargetTypeEnum.ExtendedVessel &&
    targetType !== TargetTypeEnum.BaseStation
  ) {
    return null;
  }

  const isDisabled =
    viewMode !== ViewMode.Normal && viewMode !== ViewMode.Replay;

  return (
    <>
      <div className="flex w-full justify-end">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          {isDisabled ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button id="ship-actions-button" disabled={isDisabled}>
                    Actions
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Actions are only available in map view
              </TooltipContent>
            </Tooltip>
          ) : (
            <PopoverTrigger asChild>
              <Button id="ship-actions-button">Actions</Button>
            </PopoverTrigger>
          )}
          <PopoverContent className="w-fit" side="left" align="end" sideOffset={8}>
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none">Actions</h4>
                <p className="text-sm text-muted-foreground">
                  Take one of the following actions
                </p>
              </div>
              <div className="grid gap-2"></div>
            </div>
            <div className="grid grid-cols-4">
              {(targetType === TargetTypeEnum.Ship ||
                targetType === TargetTypeEnum.ExtendedVessel) && (
                <ActionButton
                  id="show-vessel-button"
                  Icon={PiBoatThin}
                  onClick={() => onShowVesselClick()}
                  text="Show vessel image"
                ></ActionButton>
              )}
              {targetType === TargetTypeEnum.Ship && (
                <MarkTargetWithColor onSuccess={() => setIsOpen(false)} />
              )}
              {targetType === TargetTypeEnum.Ship && (
                <ActionButton
                  id="unmark-target-button"
                  Icon={PiMapPinLine}
                  onClick={() => onUnmarkTargetClick()}
                  text="Remove Mark"
                ></ActionButton>
              )}
              {targetType === TargetTypeEnum.Ship && (
                <ActionButton
                  id="download-csv-track-button"
                  Icon={PiDownload}
                  onClick={() => onDownloadTrackClick('csv')}
                  text="CSV Track"
                ></ActionButton>
              )}
              {targetType === TargetTypeEnum.Ship && (
                <ActionButton
                  id="download-kml-track-button"
                  Icon={PiDownload}
                  onClick={() => onDownloadTrackClick('kml')}
                  text="KML track"
                ></ActionButton>
              )}
              {targetType === TargetTypeEnum.Ship && (
                <CreateReportProvider>
                  <CreateReportTemplate>
                    <ShipReportButton id="ship-report-button" />
                  </CreateReportTemplate>
                </CreateReportProvider>
              )}
              {targetType === TargetTypeEnum.Ship &&
                targetInfoDetailsList.length > 0 && (
                  <ActionButton
                    id="filter-by-selected-button"
                    Icon={PiFunnelThin}
                    onClick={() => onFilterBySelectedClick()}
                    text="Filter by Selected"
                  ></ActionButton>
                )}
              {targetType === TargetTypeEnum.BaseStation && (
                <ActionButton
                  id="filter-by-basestation-button"
                  Icon={PiFunnelThin}
                  onClick={() => onFilterByBaseStationClick()}
                  text="Filter"
                ></ActionButton>
              )}
              {targetType === TargetTypeEnum.BaseStation && (
                <ActionButton
                  id="show-coverage-button"
                  Icon={PiBroadcastThin}
                  onClick={() => onShowCoverageClick()}
                  disabled={!permissions.coverage}
                  disabledText="Coverage permission required"
                  text="Show Coverage"
                ></ActionButton>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Dialog open={isShowVesselActive} onOpenChange={setIsShowVesselActive}>
        <DialogContent>
          <ShowShip
            shipInfo={targetInfo as ShipInfo}
            setIsShowVesselActive={setIsShowVesselActive}
          ></ShowShip>
        </DialogContent>
      </Dialog>
    </>
  );
};
